import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { PluginKey } from '@tiptap/pm/state';
import { MentionNodeView } from './mention.view';
import type { ExtensionMenuConfig } from '@cotera/client/app/components/app/rte/types/extension-config';

export type MentionOptions<T = unknown> = ExtensionMenuConfig & {
  HTMLAttributes: Record<string, any>;
  renderText: (props: { options: MentionOptions<T>; node: any }) => string;
  suggestion?: any;
  onClick?: (data: T) => void;
  /** Show linked-owner indicator on mentions (chat readonly use-case). */
  showOwnershipIndicator?: boolean;
  ownershipTooltipContent?: string;
};

export const MentionPluginKey = new PluginKey('mention');

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mention: {
      /**
       * Insert a mention
       */
      setMention: (attributes: {
        id: string;
        label?: string;
        color?: string;
        data?: string;
        referenceId?: string;
      }) => ReturnType;
    };
  }
}

export const CoteraMention = Node.create<MentionOptions<any>>({
  name: 'mention',

  addOptions() {
    return {
      HTMLAttributes: {},
      renderText({ options, node }) {
        return `${options.suggestion?.char ?? '@'}${
          node.attrs.label ?? node.attrs.id
        }`;
      },
      suggestion: {
        char: '@',
        pluginKey: MentionPluginKey,
      },
      showOwnershipIndicator: false,
      ownershipTooltipContent: 'This mention was added by another user.',
    };
  },

  group: 'inline',

  inline: true,

  selectable: false,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => {
          if (!attributes['id']) {
            return {};
          }

          return {
            'data-id': attributes['id'],
          };
        },
      },
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => {
          if (!attributes['label']) {
            return {};
          }

          return {
            'data-label': attributes['label'],
          };
        },
      },
      color: {
        // No default — MentionNodeView derives color from mention type (agent/document/
        // tool/…). A hard-coded default of `blue` made every pill look like a document
        // when anything still read attrs.color (HTML export, paste, older render paths).
        default: null,
        optional: true,
        parseHTML: (element) => element.getAttribute('data-color'),
        renderHTML: (attributes) => {
          if (!attributes['color']) {
            return {};
          }

          return {
            'data-color': attributes['color'],
          };
        },
      },
      data: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-data'),
        renderHTML: (attributes) => {
          if (!attributes['data']) {
            return {};
          }

          return {
            'data-data': attributes['data'],
          };
        },
      },
      referenceId: {
        default: null,
        optional: true,
        parseHTML: (element) => element.getAttribute('data-reference-id'),
        renderHTML: (attributes) => {
          if (!attributes['referenceId']) {
            return {};
          }

          return {
            'data-reference-id': attributes['referenceId'],
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(
        { 'data-type': this.name },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      this.options.renderText({
        options: this.options,
        node,
      }),
    ];
  },

  renderText({ node }) {
    return this.options.renderText({
      options: this.options,
      node,
    });
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true;
              tr.insertText(
                this.options.suggestion?.char || '',
                pos,
                pos + node.nodeSize
              );

              return false;
            }

            return true;
          });

          return isMention;
        }),
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(MentionNodeView);
  },

  addCommands() {
    return {
      setMention:
        (attributes) =>
        ({ chain }) => {
          return chain()
            .insertContent([
              {
                type: this.name,
                attrs: attributes,
              },
              {
                type: 'text',
                text: ' ',
              },
            ])
            .run();
        },
    };
  },
});
